import { Injectable, inject, NgZone } from "@angular/core";
import {
	Auth,
	signInWithEmailAndPassword,
	UserCredential,
	User as FirebaseUser,
	getAuth,
	signInWithEmailLink,
	isSignInWithEmailLink,
} from "@angular/fire/auth";
import { Observable, throwError, from, BehaviorSubject } from "rxjs";
import { environment } from "../../environments/environment";
import { Firestore, doc, updateDoc, serverTimestamp, getDoc } from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { tap, catchError, map } from "rxjs/operators";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RecaptchaService } from "./recaptcha.service";

/**
 * Copy for a failed sign-in-link request. requestSignInLink deliberately reports
 * success for an address with no account, so the failures that reach here are a
 * bad request, a transport problem, or a fault on our side — never "no such
 * user".
 */
function messageForLinkRequestError(error: { code?: string }): string {
	switch (error?.code) {
		case "functions/permission-denied":
		case "functions/unauthenticated":
			return "You don't have permission to request a sign-in link.";
		case "functions/invalid-argument":
			return "Please check the email address and try again.";
		case "functions/unavailable":
		case "functions/deadline-exceeded":
			return "We couldn't reach Matchie. Please check your connection and try again.";
		case "functions/resource-exhausted":
			return "Too many requests. Please wait a moment and try again.";
		default:
			return "We couldn't send a sign-in link just now. Please try again in a moment.";
	}
}

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private router = inject(Router);
	private snackBar = inject(MatSnackBar);

	private auth: Auth = inject(Auth);
	private zone: NgZone = inject(NgZone);
	private firestore: Firestore = inject(Firestore);
	private functions: Functions = inject(Functions);
	private recaptchaService = inject(RecaptchaService);
	public user$: Observable<FirebaseUser | null>;
	public userDoc$ = new BehaviorSubject<any | null>(null);

	constructor() {
		// Listen for auth state changes
		this.user$ = new Observable((subscriber) => {
			return this.auth.onAuthStateChanged(
				(user) => subscriber.next(user),
				(error) => subscriber.error(error),
				() => subscriber.complete(),
			);
		});

		this.user$.subscribe((user) => {
			void this.syncUserDoc(user);
		});
	}

	/**
	 * Auth state resolved (real sign-in OR a restored session on page load).
	 * We deliberately do NOT stamp last_sign_in_at here — that would mark every
	 * page reload as a sign-in. The timestamp is written only from the explicit
	 * sign-in paths (signIn / completeEmailLinkSignIn).
	 *
	 * Subscribe ignores a returned promise, so this owns its own failures.
	 */
	private async syncUserDoc(user: FirebaseUser | null): Promise<void> {
		if (!user) {
			this.userDoc$.next(null);
			return;
		}
		try {
			const userDoc = await this.fetchUserDocument(user.uid || "");
			if (userDoc) {
				this.userDoc$.next(userDoc);
			}
		} catch (error) {
			console.error("Could not load the signed-in user's document:", error);
		}
	}

	// Helper method to fetch user document from Firestore with error handling
	private async fetchUserDocument(uid: string): Promise<any | null> {
		try {
			const userDocData = await getDoc(doc(this.firestore, "users", uid));
			if (!userDocData.exists()) {
				console.warn(`User document not found for uid: ${uid}`);
				return null;
			}

			const userDoc = userDocData.data() as any;
			if (userDoc) {
				// Ensure roles is always an object
				// Include the uid in the document
				userDoc.uid = uid;
				return userDoc;
			}
			return null;
		} catch (error) {
			console.error("Error fetching user document:", error);
			return null;
		}
	}

	// Refresh the user document from Firestore
	async refreshUserDoc(): Promise<void> {
		const user = this.auth.currentUser;
		if (user) {
			const userDoc = await this.fetchUserDocument(user.uid);
			if (userDoc) {
				this.userDoc$.next(userDoc);
			}
		}
	}

	signIn(email: string, password: string): Observable<UserCredential> {
		return new Observable<UserCredential>((observer) => {
			this.zone.runOutsideAngular(() => {
				signInWithEmailAndPassword(this.auth, email, password)
					.then((result) => {
						this.zone.run(() => {
							// Real sign-in — record the timestamp.
							this.updateUserLastSignIn(result.user.uid);
							this.rememberSignedInEmail(result.user.email);
							observer.next(result);
							observer.complete();
						});
					})
					.catch((error) => {
						this.zone.run(() => {
							console.error("Sign in error:", error);
							let errorMessage = "An unknown error occurred";

							switch (error.code) {
								case "auth/invalid-email":
									errorMessage = "Invalid email format";
									break;
								case "auth/user-disabled":
									errorMessage = "This account has been disabled";
									break;
								case "auth/user-not-found":
								case "auth/wrong-password":
									errorMessage = "Invalid email or password";
									break;
								case "auth/too-many-requests":
									errorMessage =
										"Too many unsuccessful login attempts. Please try again later";
									break;
								// Newer Firebase SDKs collapse wrong-password and
								// user-not-found into invalid-credential when email
								// enumeration protection is on, so without this the most
								// ordinary failure of all fell to "An unknown error
								// occurred".
								case "auth/invalid-credential":
									errorMessage = "Invalid email or password";
									break;
								// Was only ever handled by a dead getErrorMessage in the
								// login page, which could not fire because this message
								// always won.
								case "auth/network-request-failed":
									errorMessage =
										"We couldn't reach Matchie. Please check your connection and try again.";
									break;
							}

							observer.error(new Error(errorMessage));
						});
					});
			});
		});
	}

	// Get the current authenticated user
	getCurrentUser(): FirebaseUser | null {
		return this.auth.currentUser;
	}

	// Alias for signIn for backward compatibility
	login(email: string, password: string): Observable<UserCredential> {
		return this.signIn(email, password);
	}

	// Sign out the current user
	signOut(): Promise<void> {
		return this.auth.signOut().catch((error) => {
			console.error("Error signing out:", error);
			throw error;
		});
	}

	// Helper method to retry token refresh with exponential backoff
	private async retryTokenRefresh(user: FirebaseUser, maxRetries: number = 3): Promise<boolean> {
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				// Force a token refresh to check if it's working
				await user.getIdToken(true);
				// Only log success in development mode, and only if it required retries
				if (!environment.production && attempt > 1) {
					console.warn(`Token refresh succeeded on attempt ${attempt}`);
				}
				return true;
			} catch (error: any) {
				// Always log errors, but with less detail in production
				if (!environment.production) {
					console.error(`Token refresh attempt ${attempt}/${maxRetries} failed:`, {
						code: error?.code,
						message: error?.message,
						attempt,
						error,
					});
				} else {
					console.error(`Token refresh failed (${error?.code || "unknown"})`);
				}

				// Check if this is an auth error (not a network error)
				const isAuthError =
					error?.code === "auth/invalid-user-token" ||
					error?.code === "auth/user-token-expired" ||
					error?.message?.includes("403") ||
					error?.message?.includes("Forbidden");

				// If it's an auth error and we've exhausted retries, return false
				if (isAuthError && attempt === maxRetries) {
					return false;
				}

				// If it's a network error, retry with exponential backoff
				if (!isAuthError && attempt < maxRetries) {
					const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
					if (!environment.production) {
						console.warn(`Retrying in ${delay}ms...`);
					}
					await new Promise((resolve) => setTimeout(resolve, delay));
				} else if (isAuthError) {
					// For auth errors, fail immediately
					return false;
				}
			}
		}
		return false;
	}

	// Check session validity before navigation
	// Returns true if session is valid, false if invalid (and triggers logout)
	async checkSessionBeforeNavigation(): Promise<boolean> {
		const user = this.auth.currentUser;
		if (!user) {
			return false;
		}

		const refreshSucceeded = await this.retryTokenRefresh(user, 2);
		if (!refreshSucceeded) {
			await this.zone.run(async () => {
				this.snackBar.open("Your session has expired. Please login again.", "Close", {
					duration: 5000,
				});
				await this.signOut();
				void this.router.navigate(["/login"]);
			});
			return false;
		}
		return true;
	}

	// Send sign-in link to email
	sendSignInLink(email: string, recaptchaToken: string): Observable<void> {
		// Validate the reCAPTCHA token
		if (!recaptchaToken) {
			return throwError(() => new Error("reCAPTCHA verification is required"));
		}

		// Store the email locally so you don't need to ask the user for it again
		// if they open the link on the same device.
		window.localStorage.setItem("emailForSignIn", email);

		// Call the cloud function to request sign-in link
		// The function handles user lookup and creates the pendingEmailLinks doc
		const requestSignInLinkFn = httpsCallable(this.functions, "requestSignInLink");

		return from(
			requestSignInLinkFn({
				email: email,
				recaptcha_token: recaptchaToken,
				frontend: "members",
			}),
		).pipe(
			map(() => undefined), // Convert to void
			catchError((error) => {
				console.error("Error requesting sign-in link:", error);

				// Never surface error.message. On a callable it is either Firebase's
				// own wording for a transport failure ("internal", "unavailable",
				// "Failed to fetch") or requestSignInLink's developer-facing
				// HttpsError text — neither is written for the person reading it, and
				// the fall-through meant the raw string won whenever a code was not
				// matched, which was every case except permission-denied.
				return throwError(() => new Error(messageForLinkRequestError(error)));
			}),
		);
	}

	// Update the user's lastSignInAt field in Firestore
	private updateUserLastSignIn(uid: string): void {
		try {
			// First, check if the user document exists
			const userRef = doc(this.firestore, `users/${uid}`);

			// Create an object with the fields to update
			// email_verified is only written when there is a signed-in user to read
			// it from. `?? false` would have recorded "not verified" whenever the
			// answer was simply unknown, which is a different claim.
			const currentUser = this.auth.currentUser;
			const updateData: Record<string, unknown> = {
				last_sign_in_at: serverTimestamp(),
			};
			if (currentUser) {
				updateData["email_verified"] = currentUser.emailVerified;
			}

			// Update the document
			updateDoc(userRef, updateData).catch((error) => {
				console.error(`Error updating lastSignInAt for user:`, error);
			});
		} catch (error) {
			console.error(`Exception in updateUserLastSignIn:`, error);
		}
	}

	// Check if current URL is a sign-in link
	isEmailSignInLink(url: string): boolean {
		const auth = getAuth();
		return isSignInWithEmailLink(auth, url);
	}

	// Complete sign-in with email link
	completeEmailLinkSignIn(email: string, url: string): Observable<FirebaseUser> {
		const auth = getAuth();
		return from(signInWithEmailLink(auth, email, url)).pipe(
			map((result) => result.user),
			tap((user) => {
				// Real sign-in — record the timestamp.
				this.updateUserLastSignIn(user.uid);
				this.rememberSignedInEmail(user.email);

				// Clear the stored email
				window.localStorage.removeItem("emailForSignIn");

				// Show success message
				this.snackBar.open("Successfully signed in!", "Close", {
					duration: 3000,
				});
			}),
		);
	}

	/**
	 * Records who last signed in on this browser. Deliberately outlives the
	 * session and every sign-out: it is not a credential, it is the answer to
	 * "whose device is this", which is what makes an unintentional user switch
	 * detectable. `emailForSignIn` cannot answer that — it is deleted the moment
	 * a sign-in succeeds, so a browser signed in yesterday has nothing on record.
	 */
	private rememberSignedInEmail(email: string | null): void {
		if (email) {
			window.localStorage.setItem("lastSignedInEmail", email);
		}
	}

	/**
	 * The address this browser is known to belong to: an in-flight self-requested
	 * sign-in first, otherwise whoever last signed in here. Null on a browser
	 * that has never been used to sign in, where there is no switch to detect.
	 */
	knownEmailForBrowser(): string | null {
		return (
			window.localStorage.getItem("emailForSignIn") ||
			window.localStorage.getItem("lastSignedInEmail")
		);
	}

	// Get state from URL
	getSignInLinkState(url: string): any {
		try {
			const params = new URLSearchParams(new URL(url).search);
			const stateStr = params.get("state");
			return stateStr ? JSON.parse(decodeURIComponent(stateStr)) : null;
		} catch (e) {
			console.error("Error parsing sign-in link state:", e);
			return null;
		}
	}

	// Sign out the current user and navigate to login
	async logout(): Promise<void> {
		try {
			await this.signOut();
			void this.router.navigate(["/login"]);
		} catch (error) {
			console.error("Error during logout:", error);
		}
	}
}
